import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bmoApi } from '../services/api';
import { colors } from '../theme';
import { Card, StatCard, SectionHeader, Spinner, AlertBanner, EmptyState } from '../components/common';
import { format } from 'date-fns';

const SEVERITY_CFG = {
  HIGH:   { bg: colors.error[50],   border: colors.error[200],   text: colors.error[700],   label:'Critical', dot: colors.error[600]   },
  MEDIUM: { bg: colors.warning[50], border: colors.warning[200], text: colors.warning[700], label:'Warning',  dot: colors.warning[600] },
  LOW:    { bg: colors.neutral[50], border: colors.neutral[200], text: colors.neutral[600], label:'Info',     dot: colors.neutral[400] },
};

const TYPE_ICONS = {
  fridge_breach: '🌡️',
  overdue_record:'📋',
  cert_expired:  '🎖️',
  bmw_gap:       '🗑️',
  score_drop:    '📉',
  vacc_gap:      '💉',
  system:        '⚙️',
};

export default function AlertsPage() {
  const navigate = useNavigate();
  const [alerts,  setAlerts]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [filter,  setFilter]  = useState('all');

  useEffect(() => {
    bmoApi.getAlerts()
      .then((d) => setAlerts(d?.alerts || []))
      .catch((e) => setError(e?.message || 'Could not load alerts.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner size={48} />;
  if (error)   return <AlertBanner type="error" message={error} />;

  const high   = alerts.filter((a) => a.severity === 'HIGH').length;
  const medium = alerts.filter((a) => a.severity === 'MEDIUM').length;
  const filtered = filter === 'all' ? alerts : alerts.filter((a) => a.severity === filter);

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ margin:'0 0 4px', fontSize:24, fontWeight:700, color: colors.text.primary }}>🔔 Alerts</h1>
        <p style={{ margin:0, fontSize:14, color: colors.text.secondary }}>Active compliance alerts across all facilities</p>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:16, marginBottom:24 }}>
        <StatCard icon="🔴" label="Critical"  value={high}         color={colors.error[700]}   bg={colors.error[50]} />
        <StatCard icon="🟡" label="Warnings"  value={medium}       color={colors.warning[700]} bg={colors.warning[50]} />
        <StatCard icon="📋" label="Total"     value={alerts.length} color={colors.primary[700]} />
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {[['all','All'],['HIGH','Critical'],['MEDIUM','Warnings'],['LOW','Info']].map(([key, lbl]) => (
          <button key={key} onClick={() => setFilter(key)}
            style={{ padding:'6px 14px', borderRadius:99, border:'none', cursor:'pointer', fontSize:13, fontWeight:500, background: filter === key ? colors.primary[700] : colors.neutral[100], color: filter === key ? colors.white : colors.text.secondary }}>
            {lbl}
          </button>
        ))}
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {filtered.length === 0
          ? <Card><EmptyState icon="✅" title="No alerts" subtitle="All facilities are on track" /></Card>
          : filtered.map((alert, idx) => {
              const cfg  = SEVERITY_CFG[alert.severity] || SEVERITY_CFG.LOW;
              const icon = TYPE_ICONS[alert.type] || '⚠️';
              return (
                <div key={idx} style={{ background: cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'flex-start', gap:14 }}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background: cfg.dot, marginTop:4, flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      <span style={{ fontSize:18 }}>{icon}</span>
                      <span style={{ fontSize:14, fontWeight:600, color: cfg.text }}>{alert.title}</span>
                      <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:99, background: cfg.border, color: cfg.text }}>{cfg.label}</span>
                    </div>
                    <div style={{ fontSize:13, color: colors.text.secondary, marginBottom:4 }}>{alert.message}</div>
                    <div style={{ display:'flex', alignItems:'center', gap:12, fontSize:12, color: colors.text.secondary }}>
                      {alert.facility_name && <span>🏥 {alert.facility_name}</span>}
                      {alert.created_at    && <span>🕐 {format(new Date(alert.created_at), 'dd MMM, hh:mm a')}</span>}
                    </div>
                  </div>
                  {alert.facility_id && (
                    <button onClick={() => navigate(`/facilities/${alert.facility_id}`)}
                      style={{ padding:'6px 12px', background: colors.white, border:`1px solid ${cfg.border}`, borderRadius:6, fontSize:12, cursor:'pointer', color: cfg.text, fontWeight:500, flexShrink:0 }}>
                      View →
                    </button>
                  )}
                </div>
              );
            })
        }
      </div>
    </div>
  );
}
